/**
 * Modal form submission utilities
 */

import { SDKError } from '../types';
import { decodeBase64 } from './helpers';

/**
 * Creates hidden input fields from a data object
 */
export function createFormFields(
  formElement: HTMLFormElement,
  data: Record<string, unknown>,
  prefix = ''
): void {
  for (const [key, value] of Object.entries(data)) {
    const fieldName = prefix ? `${prefix}[${key}]` : key;

    if (value === null || value === undefined) {
      // Skip null/undefined values
      continue;
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Recursively handle nested objects
      createFormFields(formElement, value as Record<string, unknown>, fieldName);
    } else if (Array.isArray(value)) {
      // Handle arrays
      value.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          createFormFields(formElement, item as Record<string, unknown>, `${fieldName}[${index}]`);
        } else {
          const input = document.createElement('input');
          input.setAttribute('type', 'hidden');
          input.setAttribute('name', `${fieldName}[${index}]`);
          input.setAttribute('value', String(item));
          formElement.appendChild(input);
        }
      });
    } else {
      // Create hidden input for primitive values
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', fieldName);
      input.setAttribute('value', String(value));
      formElement.appendChild(input);
    }
  }
}

/**
 * Submits the form to load 3DS authentication page in iframe
 * Decodes base64 data, parses JSON, and creates hidden form fields
 */
export function submitRedirectForm(
  formElement: HTMLFormElement,
  url: string,
  method: string,
  base64Data: string,
  add3dsSdkFlag = false,
  log?: (message: string, ...args: unknown[]) => void
): void {
  // Set form action and method
  formElement.setAttribute('action', url);
  formElement.setAttribute('method', method.toUpperCase());

  // Clear any existing inputs
  formElement.innerHTML = '';

  try {
    // Decode base64 data
    log?.('Decoding base64 data');
    const decodedData = decodeBase64(base64Data);

    // Parse JSON object
    log?.('Parsing JSON data');
    const parsedData = JSON.parse(decodedData) as Record<string, unknown>;

    // Adding flag so that paymentengine know it come from 3ds sdk
    if (add3dsSdkFlag) {
      parsedData.from3dsSdk = true;
    }

    // Create hidden input fields from parsed JSON object
    log?.('Creating form fields from parsed data', parsedData);
    createFormFields(formElement, parsedData);

    // Submit the form
    log?.('Submitting form to', url, 'with method', method);
    formElement.submit();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    log?.('Error processing form data', error);
    throw new SDKError(
      `Failed to process form data: ${errorMessage}`,
      'FORM_DATA_PROCESSING_FAILED'
    );
  }
}

/**
 * Submits form to iframe for action flow
 * Creates a form that submits to the action URL with the value object as JSON string
 * The form will submit to the modal iframe, similar to redirect flow
 */
export function submitActionForm(
  formElement: HTMLFormElement,
  iframeElement: HTMLIFrameElement,
  actionUrl: string,
  value: Record<string, unknown>,
  log?: (message: string, ...args: unknown[]) => void
): void {
  // Get the iframe name to target it
  const iframeName = iframeElement.getAttribute('name');
  if (!iframeName) {
    throw new SDKError('Iframe name not found', 'IFRAME_NAME_NOT_FOUND');
  }

  log?.('Submitting action form to', actionUrl, 'with value object', value);

  // Set form action, method, and target (iframe instead of _top)
  formElement.setAttribute('action', actionUrl);
  formElement.setAttribute('method', 'post');
  formElement.setAttribute('target', iframeName);
  formElement.setAttribute('name', 'form1');

  // Clear any existing inputs
  formElement.innerHTML = '';

  // Convert the entire value object to JSON string
  const valueJsonString = JSON.stringify(value);
  log?.('Converted value object to JSON string', valueJsonString);

  // Create hidden input for data with the JSON stringified value
  const dataInput = document.createElement('input');
  dataInput.setAttribute('type', 'hidden');
  dataInput.setAttribute('name', 'data');
  dataInput.setAttribute('value', valueJsonString);
  dataInput.setAttribute('id', 'data');
  formElement.appendChild(dataInput);

  // Auto-submit the form after a brief delay to ensure it's in the DOM
  // Using setTimeout to ensure the form is fully rendered
  setTimeout(() => {
    log?.('Auto-submitting form to', actionUrl);
    formElement.submit();
  }, 100);
}
